import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Id } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import {
  BaseLogo,
  getLogoType,
  getProductPrice,
  getDesignProductPrice,
} from "../../../../core/models/entities/BaseLogo";
import { useCartContext } from "../../../../contexts/CartContext";
import {
  trackBeginCheckout,
  trackExcelUpload,
} from "../../../../utils/functions/analytics";
import {
  CartItem,
  CartItemDesign,
  CartItemDesignInstance,
  CartItemDesignInstanceLogo,
  CartItemLogo,
  QuantityRow,
  getCartItemsCurrency,
} from "../models/CartItem";
import { Design, DesignLogo } from "../../../../core/models/entities/Design";
import {
  DeliveryType,
  Order,
  OrderDeliveryPreferences,
  OrderLine,
  OrderLineDesign,
  OrderLineLogo,
  OrderStatus,
  Packaging,
} from "../../../../core/models/entities/Order";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { CasingOption } from "../../../shared/functions/casing";
import {
  ExtraLogo,
  HeatingOption,
} from "../../order-confirmation/dialogs/add-heating-fee-order-dialog";
import {
  dissmissToast,
  useErrorToast,
  useLoadingToast,
  withToasts,
} from "../../../components/Toast/Toast";
import {
  DesignTemplate,
  LogoTemplate,
} from "../../../../core/models/entities/DesignTemplate";
import { OrderDto } from "../../../../data/models/responses/OrderDto";
import { PlaceOrderAckDto } from "../../../../data/models/responses/PlaceOrderAckDto";
import { VerifyStorageRowDto } from "../../../../data/models/responses/VerifyStorageResponseDto";
import {
  packagingFeesFromOrder,
  patchStorageCounts,
} from "../../order-confirmation/storageVerify";
import { actAsCacheKey } from "../../../../contexts/actAsStore";
import { storageFieldsUpdater } from "../../../../utils/functions/storageFreshness";
import { getDesignLogoPlacementMap } from "../functions/getDesignLogoPlacement";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useOrganizationImpersonationContext } from "../../../../contexts/OrganizationImpersonationContext";
import { enviroment } from "../../../../enviroment";
import { patchPendingProducts } from "../../../../utils/functions/productPending";
import { withNavUnavailableRetry } from "../../../../data/clients/navUnavailableRetry";
import { Price } from "../../../../core/models/entities/Price";
import { Organization } from "../../../../core/models/entities/Organization";
import { Profile } from "../../../../core/models/entities/Profile";

import { useApiClient } from "../../../../data/clients/ApiClient";
import {
  openOversizeSheetDialog,
  useOversizeSheetGuard,
} from "../../../../hooks/useOversizeSheetGuard";
import {
  collectOversizeFromDesigns,
  collectOversizeFromLogos,
  isSheetSizeUnavailableError,
  OversizeAddOutcome,
  OversizeLogoInfo,
  sheetSizeUnavailableCanRequest,
  sheetSizeUnavailableLogos,
} from "../../../../utils/functions/oversizeSheet";

// Org VAT rates are stored as PERCENT in the DB (25.00 = 25%; 0.00 = genuine
// zero VAT / reverse charge, e.g. Dutch B2B vendors). Explicit 0 is honored;
// only a missing value (placeholder org before the profile loads) falls back
// to the Danish default. Tolerates a legacy fraction (0.25) defensively.
export function resolveVatFraction(vatPercent: number | undefined | null): number {
  if (vatPercent === undefined || vatPercent === null) {
    return 0.25;
  }
  return vatPercent > 1 ? vatPercent / 100 : vatPercent;
}

// Name and number logos share the same SKU across all typed values, so the
// size suffix adds noise without adding identity. Static logos (TextLogo,
// ImageLogo, SymbolLogo) keep the size — it distinguishes variants that may
// otherwise look the same in the list.
function formatLogoTitleWithSize(
  title: string,
  baseLogo: BaseLogo | undefined,
): string {
  if (!baseLogo) return title;
  const skipSize =
    baseLogo.logoType === "NameLogo" || baseLogo.logoType === "NumberLogo";
  return skipSize
    ? title
    : `${title} ${baseLogo.widthMm}x${baseLogo.heightMm}mm`;
}

// Merge a quantity into a label-keyed row list (digits for number logos,
// typed values for name logos), accumulating on an existing row when the
// label is already present.
function addQuantityRow(
  rows: QuantityRow[],
  label: string,
  quantity: number,
): void {
  if (quantity <= 0) return;
  const existingRow = rows.find((q) => q.label === label);
  if (existingRow) existingRow.quantity += quantity;
  else rows.push({ label, quantity });
}

export function useOrderActionHandler() {
  const { t } = useTranslation();
  const {
    addLogoItem: addLogoToCart,
    addLogoItems: addLogosToCart,
    addDesignItem: addDesignToCart,
    addDesignItems: addDesignsToCart,
    removeItem: removeFromCart,
    updateItem: updateInCart,
    clearItems,
    items,
    designs,
  } = useCartContext();
  const { orderRepository } = useRepositoriesContext();
  const {
    addItems,
    activeOrder,
    setActiveOrder,
    checkoutItems,
    setCheckoutItems,
    heatingOption,
  } = useCartContext();
  const { currency, organization, profile } = useProfileContext();
  const queryClient = useQueryClient();
  // Checkout storage verify (flag checkoutStorageVerifyEnabled): the cart and
  // checkout sets as of the latest render, for code that awaits NAV before it
  // builds a draft - the verify answer may have patched the sets meanwhile.
  const latestItemsRef = useRef({ checkoutItems, items });
  latestItemsRef.current = { checkoutItems, items };
  // Admin act-as-vendor: when active, the placed order is attributed to the
  // selected vendor user via CommitOrderDto.OnBehalfOfUserId (admin-only, API-validated).
  const { user: actAsUser, organizationId: actAsOrgId } =
    useOrganizationImpersonationContext();

  const [isLoading, setIsLoading] = useState(false);
  const startLoading = () => setIsLoading(true);
  const stopLoading = () => setIsLoading(false);
  const navigate = useNavigate();

  // Oversize sheet guard (flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED): for
  // the vendors the API lists, an IMAGE logo over the API's limit is not put
  // in the cart - the dialog opens instead (context "add"), so no price
  // check POST and no stock lookup leave for it. Reorder/change-order keep
  // the other lines and open the dialog for the offending ones (context
  // "reorder"); when EVERY line is refused nothing is put in the cart and
  // reorder does not navigate to the checkout. The API's own 409
  // sheetSizeUnavailable at commit/place is caught below and opens the same
  // dialog (context "place"); the cart is left as is. The add handlers
  // answer an OversizeAddOutcome {added, refused} so their callers show no
  // "Added to cart" for a refused logo/design. Flag off: guard.active is
  // false, every branch is skipped, refused is always 0 and
  // isSheetSizeUnavailableError is always false - byte-identical.
  const oversizeGuard = useOversizeSheetGuard();

  function oversizeInLogos(logos: BaseLogo[]): OversizeLogoInfo[] {
    if (!enviroment.oversizeSheetGuardEnabled || !oversizeGuard.active) return [];
    return collectOversizeFromLogos(logos, oversizeGuard.maxMm, oversizeGuard.bothSides);
  }

  // Designs exempt (the hint's designsExempt = API setting
  // OversizeLogoSheetDesignsExemptEnabled, Nichlas 28/9: «Design skal ikke
  // påvirkes af dette»): a design is never refused - only standalone logo
  // lines are judged. Absent from the hint = false = today.
  function oversizeInDesigns(designList: Design[]): OversizeLogoInfo[] {
    if (!enviroment.oversizeSheetGuardEnabled || !oversizeGuard.active) return [];
    if (oversizeGuard.designsExempt) return [];
    return collectOversizeFromDesigns(designList, oversizeGuard.maxMm, oversizeGuard.bothSides);
  }

  // Splits cart items into the ones that may go in and the offending logos
  // (a logo line with an oversize image logo, or a design line placing one;
  // designsExempt = design lines are always kept).
  function splitOversizeCartItems(cartItems: CartItem[]): {
    kept: CartItem[];
    offending: OversizeLogoInfo[];
  } {
    if (!enviroment.oversizeSheetGuardEnabled || !oversizeGuard.active) {
      return { kept: cartItems, offending: [] };
    }
    const kept: CartItem[] = [];
    const offending: OversizeLogoInfo[] = [];
    cartItems.forEach((item) => {
      const found =
        item.type === "Logo"
          ? collectOversizeFromLogos(
              [(item as CartItemLogo).baseLogo],
              oversizeGuard.maxMm,
              oversizeGuard.bothSides,
            )
          : item.type === "Design" && !oversizeGuard.designsExempt
            ? collectOversizeFromDesigns(
                [(item as CartItemDesign).design],
                oversizeGuard.maxMm,
                oversizeGuard.bothSides,
              )
            : [];
      if (found.length === 0) kept.push(item);
      else found.forEach((f) => offending.push(f));
    });
    return { kept, offending };
  }

  // The 409 sheetSizeUnavailable of CommitOrder/PlaceOrder: open the dialog
  // (context "place") and rethrow, so the caller's chain never reaches its
  // success branch (cart clear, receipt). Any other error passes through.
  function rethrowWithOversizeDialog(error: unknown): never {
    if (isSheetSizeUnavailableError(error)) {
      openOversizeSheetDialog(sheetSizeUnavailableLogos(error), "place", {
        ...oversizeGuard.dialogOptions,
        hideRequest: !sheetSizeUnavailableCanRequest(error),
      });
    }
    throw error;
  }

  function updateLogoOrderLine(
    logo: BaseLogo,
    deliveryQuantities?: QuantityRow[],
    storageQuantities?: QuantityRow[],
    casing?: CasingOption,
  ) {
    const exists = items.find((x) => x.id === "" + logo.id) as CartItemLogo;
    const updatedOrderLine = {
      ...exists,
      deliveryQuantities: deliveryQuantities ?? exists?.deliveryQuantities ?? 0,
      storageQuantities: storageQuantities ?? exists?.storageQuantities ?? 0,
      casing: casing ?? exists?.casing,
    } as CartItemLogo;

    updateInCart({ ...updatedOrderLine });
  }

  function handleLogoAddOrderLine(
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
  ): OversizeAddOutcome {
    const oversize = oversizeInLogos([baseLogo]);
    if (oversize.length > 0) {
      openOversizeSheetDialog(oversize, "add", oversizeGuard.dialogOptions);
      return { added: 0, refused: 1 };
    }
    const existingLogo = items.find((x) => x.id === baseLogo.id.toString()) as
      | CartItemLogo
      | undefined;

    const newLogo = mapBaseLogoToCartItemLogo(
      baseLogo,
      existingLogo ? existingLogo.toDelivery + toDelivery : toDelivery,
      existingLogo ? existingLogo.toStorage + toStorage : toStorage,
    );

    addLogoToCart(newLogo)
      .then()
      .catch(() => {
        useErrorToast(t("orderCreate.cantMixVendorProducts"));
      });
    return { added: 1, refused: 0 };
  }

  function handleLogoAddOrderLines(
    baseLogos: BaseLogo[],
    toDelivery: number,
    toStorage: number,
  ): OversizeAddOutcome {
    let refusedCount = 0;
    const oversize = oversizeInLogos(baseLogos);
    if (oversize.length > 0) {
      openOversizeSheetDialog(oversize, "add", oversizeGuard.dialogOptions);
      const refused = new Set(oversize.map((o) => o.logoId));
      const total = baseLogos.length;
      baseLogos = baseLogos.filter((l) => !refused.has(l.id));
      refusedCount = total - baseLogos.length;
      if (baseLogos.length === 0) return { added: 0, refused: refusedCount };
    }
    const newLogos = baseLogos.map((baseLogo) => {
      const existingLogo = items.find(
        (x) => x.id === baseLogo.id.toString(),
      ) as CartItemLogo | undefined;

      const newLogo = mapBaseLogoToCartItemLogo(
        baseLogo,
        existingLogo ? existingLogo.toDelivery + toDelivery : toDelivery,
        existingLogo ? existingLogo.toStorage + toStorage : toStorage,
      );

      return newLogo;
    });

    addLogosToCart(newLogos)
      .then()
      .catch(() => {
        useErrorToast(t("orderCreate.cantMixVendorProducts"));
      });
    return { added: newLogos.length, refused: refusedCount };
  }

  function mapBaseLogoToCartItemLogo(
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
  ): CartItemLogo {
    const itemLogo: CartItemLogo = {
      id: baseLogo.id.toString(),
      title: baseLogo.title,
      type: "Logo",
      displayImage: baseLogo.displayImage!,
      discriminator: baseLogo.logoType,
      baseLogo: baseLogo,
      toStorage: toStorage,
      toDelivery: toDelivery,
      value: undefined,
      logoId: baseLogo.id,
      logoVariant: getLogoType(baseLogo),
      isNumeric: baseLogo.logoType === "NumberLogo",
      deliveryQuantities:
        toDelivery > 0 ? [{ label: "", quantity: toDelivery }] : [],
      storageQuantities:
        toStorage > 0 ? [{ label: "", quantity: toStorage }] : [],
    };

    return itemLogo;
  }

  function handleDesignAddOrderLine(
    design: Design,
    quantity: number,
    options?: { size?: string; label?: string },
    excludedDeletedDesignLogos?: DesignLogo[],
  ): OversizeAddOutcome {
    const oversize = oversizeInDesigns([design]);
    if (oversize.length > 0) {
      openOversizeSheetDialog(oversize, "add", oversizeGuard.dialogOptions);
      return { added: 0, refused: 1 };
    }
    const itemQuantity = items.find((x) => +x.id === design.id) as
      | CartItemDesign
      | undefined;
    const placeholderLogos = design.designLogos;
    const placementLabels = getDesignLogoPlacementMap(design);

    const instances = [
      ...(itemQuantity?.instances ?? []),
      {
        logos: placeholderLogos.map(
          (x) =>
            ({
              logoVariant: getLogoType(x.logo),
              locationIdentifier:
                placementLabels.get(x.inDesignId) ??
                x.viewName?.toUpperCase?.() ??
                "",
              value: "",
              logoId: x.logo.id,
              baseLogo: x.logo,
              designLogoInDesignId: x.inDesignId,
            }) as CartItemDesignInstanceLogo,
        ),
        label: options?.label ?? "New Label",
        toDelivery: quantity,
        toStorage: 0,
        size: options?.size ?? "M",
      },
    ];

    const cartItemDesign = mapDesignToCartItemLogo(
      design,
      instances,
      excludedDeletedDesignLogos,
    );

    addDesignToCart(cartItemDesign)
      .then()
      .catch(() => {
        useErrorToast(t("orderCreate.cantMixVendorProducts"));
      });
    return { added: 1, refused: 0 };
  }

  function mapDesignToCartItemLogo(
    design: Design,
    instances: CartItemDesignInstance[],
    excludedDeletedDesignLogos?: DesignLogo[],
  ): CartItemDesign {
    const newDesign: CartItemDesign = {
      id: design.id.toString(),
      title: design.title,
      type: "Design",
      displayImage: design.displayImage!,
      design: design,
      instances: instances,
      excludedDeletedDesignLogos,
    };
    return newDesign;
  }

  function handleDesignAddOrderLines(
    designs: Design[],
    quantity: number,
    options?: { size?: string; label?: string },
    excludedDeletedDesignLogos?: Map<number, DesignLogo[]>,
  ): OversizeAddOutcome {
    let refusedCount = 0;
    const oversize = oversizeInDesigns(designs);
    if (oversize.length > 0) {
      openOversizeSheetDialog(oversize, "add", oversizeGuard.dialogOptions);
      const refused = new Set(oversize.map((o) => o.designId));
      const total = designs.length;
      designs = designs.filter((d) => !refused.has(d.id));
      refusedCount = total - designs.length;
      if (designs.length === 0) return { added: 0, refused: refusedCount };
    }
    const newDesigns = designs.map((design) => {
      const itemQuantity = items.find((x) => +x.id === design.id) as
        | CartItemDesign
        | undefined;
      const placeholderLogos = design.designLogos;
      const placementLabels = getDesignLogoPlacementMap(design);
      const newDesign = {
        id: design.id.toString(),
        quantity: quantity,
        title: design.title,
        type: "Design",
        displayImage: design.displayImage!,
        design: design,
        logosToStorage: 0,
        logosToDelivery: quantity,
        excludedDeletedDesignLogos: excludedDeletedDesignLogos?.get(design.id),
        instances: [
          ...(itemQuantity?.instances ?? []),
          {
            isNumeric: false,
            logos: placeholderLogos.map(
              (x) =>
                ({
                  logoVariant: getLogoType(x.logo),
                  locationIdentifier:
                    placementLabels.get(x.inDesignId) ??
                    x.viewName?.toUpperCase?.() ??
                    "",
                  value: "",
                  logoId: x.logo.id,
                  baseLogo: x.logo,
                  designLogoInDesignId: x.inDesignId,
                }) as CartItemDesignInstanceLogo,
            ),
            label: options?.label ?? "Temp",
            toDelivery: quantity,
            toStorage: 0,
            size: options?.size ?? "M",
          },
        ],
      } as CartItemDesign;

      return newDesign;
    });

    addDesignsToCart(newDesigns)
      .then()
      .catch(() => {
        useErrorToast(t("orderCreate.cantMixVendorProducts"));
      });
    return { added: newDesigns.length, refused: refusedCount };
  }

  function handleRemoveOrderLine(id: string) {
    removeFromCart(id);
  }

  function handleLogoSetQuantities(
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
  ) {
    const exists = items.find((x) => x.id === "" + baseLogo.id);
    if (!exists) {
      handleLogoAddOrderLine(baseLogo, toDelivery, toStorage);
      return;
    }

    const updatedLogo = {
      ...exists,
      toDelivery: toDelivery,
      toStorage: toStorage,
    } as CartItemLogo;

    updateInCart({ ...updatedLogo });
  }

  function handlePlaceVerdict(order: Order, verdict: Verdict) {
    const loadingMessage = verdict.approve
      ? t("orderCreate.placingOrderLong")
      : t("orderCreate.rejectingOrder");
    const successMessage = verdict.approve
      ? t("orderCreate.orderPlacedSuccess")
      : t("orderCreate.verdictSuccess");

    withToasts(
      () => orderRepository.placeVerdict(order, verdict),
      loadingMessage,
      successMessage,
      t("orderCreate.orderPlaceFailed"),
      setIsLoading,
    );
  }

  function handleDesignInstanceUpdateStorage(orderlineDesign: CartItemDesign) {
    const newOrderLineDesign = { ...orderlineDesign } as CartItemDesign;

    if (newOrderLineDesign.instances === undefined) {
      newOrderLineDesign.instances = [];
    }

    updateInCart({ ...newOrderLineDesign });
  }

  async function handleCreateOrder(
    orgId: number,
    items: CartItem[],
    heatingOption: HeatingOption,
    extraLogos: ExtraLogo[],
  ): Promise<Order> {
    setIsLoading(true);
    const heatingOrderLines = applyHeatingOption(items, heatingOption);
    const extraOrderLines = applyExtraLogos(heatingOrderLines, extraLogos);
    // Persist the transformed items (pre-variant-expansion: place/commit run
    // applyLogoVariantLines themselves, and it is not idempotent for name
    // logos). The confirmation page draft below and the eventual API payload
    // are both derived from this exact set, so what is shown is what is sent.
    setCheckoutItems(extraOrderLines);
    // Only the draft copy carries the "Application" display marking; the
    // payload (checkoutItems) keeps the cart's own deliveryType untouched -
    // the server maps "Application" to NAV direct quantities, so letting it
    // reach the API would flip normal heated orders from shipment to direct.
    const draftOrderLines = markHeatingDeliveryTypeForDraft(
      extraOrderLines,
      heatingOption,
    );
    const allLogoOrderLines = applyLogoVariantLines(draftOrderLines);

    // NOTE: This snippet filters name logos with empty inputs. Thankfully, it is not needed at the moment...
    // const filteredOrderLines = allLogoOrderLines.filter((item) =>
    //   item.type !== 'Logo' ||
    //   !['name', 'number'].includes((item as CartItemLogo).logoVariant ?? 'name') ||
    //   !!(item as CartItemLogo).value
    // )

    // Build a local draft Order to render the confirmation page instantly.
    // Label the draft with the currency of the price ROWS being summed, not
    // the profile currency: the draft never converts amounts, so a profile
    // label on foreign-currency rows fabricates totals (act-as EUR header on
    // DKK amounts). Falls back to profile currency / "DKK" when rows carry
    // no currency — identical to before for DKK orgs.
    const draftOrder = buildLocalOrderDraft(
      orgId,
      allLogoOrderLines,
      getCartItemsCurrency(allLogoOrderLines, currency),
      organization,
      profile,
    );
    setActiveOrder(draftOrder);
    setIsLoading(false);
    trackBeginCheckout(allLogoOrderLines.length);
    return Promise.resolve(draftOrder);
  }

  function applyHeatingOption(
    orderlines: CartItem[],
    heatingOption: HeatingOption,
  ): CartItem[] {
    // With heating (all/partly) the transfers are applied to the garments and
    // the order is sent exactly as the cart describes it. The "Application"
    // marking shown on the confirmation draft is display-only and applied in
    // markHeatingDeliveryTypeForDraft - it must never reach the payload.
    if (heatingOption !== HeatingOption.none) {
      return orderlines;
    }

    const logos = orderlines.filter((x) => x.type === "Logo") as CartItemLogo[];
    const designs = orderlines
      .filter((x) => x.type === "Design")
      .map((x) => x as CartItemDesign);

    // Clone existing logos - including their quantity rows - so neither the
    // design flattening below nor applyExtraLogos mutates the cart state.
    const newLogos = logos.map((l) => ({
      ...l,
      deliveryQuantities: (l.deliveryQuantities ?? []).map((q) => ({ ...q })),
      storageQuantities: (l.storageQuantities ?? []).map((q) => ({ ...q })),
    }));
    // Original standalone logo line -> its checkout clone, so the final
    // assembly can keep every retained line at its original position.
    const cloneByOriginalLogo = new Map<CartItem, CartItemLogo>();
    logos.forEach((l, i) => cloneByOriginalLogo.set(l, newLogos[i]));
    const preservedDesigns = new Set<CartItemDesign>();

    designs.forEach((designItem) => {
      const isExplicitTransferLogo = (l: CartItemDesignInstanceLogo) => {
        const designLogo = designItem.design.designLogos?.find(
          (d) => d.logoId === l.logoId,
        );
        const baseLogo = designLogo?.logo ?? l.baseLogo;
        return baseLogo?.transferMethod?.type?.toLowerCase() === "transfer";
      };

      // Only a design whose every instance logo is explicitly a heat
      // transfer can be reduced to loose transfers. Anything else -
      // embroidery, screen/direct print, missing or unknown transfer
      // method, or instances without logos - cannot be delivered as loose
      // transfers, so the design is kept whole; flattening it would
      // silently drop the garment line from the order.
      const isPureTransferDesign =
        designItem.instances.length > 0 &&
        designItem.instances.every(
          (inst) =>
            inst.logos.length > 0 && inst.logos.every(isExplicitTransferLogo),
        );
      if (!isPureTransferDesign) {
        preservedDesigns.add(designItem);
        return;
      }

      designItem.instances.forEach((instance) => {
        const instanceToDelivery = instance.toDelivery || 0;
        const instanceToStorage = instance.toStorage || 0;
        if (instanceToDelivery + instanceToStorage <= 0) return;

        instance.logos.forEach((instanceLogo) => {
          const designLogo = designItem.design.designLogos?.find(
            (d) => d.logoId === instanceLogo.logoId,
          );
          // Use fresh backend data if available, otherwise fallback to baseLogo
          const baseLogo = designLogo?.logo ?? instanceLogo.baseLogo;

          if (!baseLogo) return;

          // Find existing logo in our accumulation list
          let existingLogo = newLogos.find((l) => l.logoId === baseLogo.id);

          if (!existingLogo) {
            // Create new logo item
            existingLogo = mapBaseLogoToCartItemLogo(baseLogo, 0, 0);
            // Clear default quantity created by mapBaseLogoToCartItemLogo because we will add specific quantities
            existingLogo.deliveryQuantities = [];
            existingLogo.storageQuantities = [];
            newLogos.push(existingLogo);
          }

          const targetLogo = existingLogo;

          // Update quantities
          // "No Heating" means we deliver the transfers. The instance's
          // storage split is preserved as toStorage so the standalone flow
          // in the API creates the matching storage lines.
          targetLogo.toDelivery += instanceToDelivery;
          targetLogo.toStorage += instanceToStorage;

          // Handle variable data (Name/Number)
          if (
            baseLogo.logoType === "NameLogo" ||
            baseLogo.logoType === "NumberLogo"
          ) {
            const value = instanceLogo.value?.toString() || "";
            // Number logo rows are keyed per digit ("0"-"9"), matching the
            // standalone number flow; name logo rows are keyed on the value.
            const labels =
              baseLogo.logoType === "NumberLogo" ? value.split("") : [value];
            labels.forEach((label) => {
              addQuantityRow(
                targetLogo.deliveryQuantities,
                label,
                instanceToDelivery,
              );
              addQuantityRow(
                targetLogo.storageQuantities,
                label,
                instanceToStorage,
              );
            });
          }
        });
      });
    });

    // Keep the cart's original line order: standalone logos stay in place
    // (with any flattened design quantities merged in), preserved designs
    // stay in place, flattened designs are removed, and logos that only
    // exist because a design was flattened are appended last.
    const orderedLines: CartItem[] = [];
    orderlines.forEach((line) => {
      if (line.type === "Logo") {
        orderedLines.push(cloneByOriginalLogo.get(line)!);
      } else if (preservedDesigns.has(line as CartItemDesign)) {
        orderedLines.push(line);
      }
    });
    return [...orderedLines, ...newLogos.slice(logos.length)];
  }

  // Display-only companion to applyHeatingOption: the confirmation draft
  // marks design instance logos as "Application" when the transfers will be
  // heated on, so buildLocalOrderDraft renders them as applied. This marked
  // copy is only ever fed to the local draft - it must never reach the API
  // payload, where "Application" flips NAV from shipment to direct quantities.
  function markHeatingDeliveryTypeForDraft(
    orderlines: CartItem[],
    heatingOption: HeatingOption,
  ): CartItem[] {
    if (heatingOption === HeatingOption.none) return orderlines;

    return orderlines.map((line) => {
      if (line.type !== "Design") return line;
      const design = line as CartItemDesign;
      return {
        ...design,
        instances: design.instances.map((instance) => {
          return {
            ...instance,
            logos: instance.logos.map((logo) => {
              return {
                ...logo,
                deliveryType: "Application",
              };
            }) as CartItemDesignInstanceLogo[],
          };
        }) as CartItemDesignInstance[],
      };
    });
  }

  function applyExtraLogos(orderlines: CartItem[], extraLogos: ExtraLogo[]) {
    const existingLogoIds = new Set(
      (orderlines.filter((l) => l.type === "Logo") as CartItemLogo[]).map(
        (l) => l.baseLogo.id,
      ),
    );

    // Digit labels for a number-logo extra. An ExtraLogo carries no typed
    // value, so the extra quantity is read as "more copies of the numbers
    // already in the order": reuse the digits from the standalone line's
    // quantity rows and from design instance values. The payload mapper only
    // forwards digit-keyed rows for number logos, so an un-keyed row would be
    // silently dropped from the order.
    const digitLabelsFor = (logoId: number): string[] => {
      const labels = new Set<string>();
      orderlines.forEach((line) => {
        if (line.type === "Logo") {
          const logoLine = line as CartItemLogo;
          if (logoLine.baseLogo.id !== logoId) return;
          [
            ...(logoLine.deliveryQuantities ?? []),
            ...(logoLine.storageQuantities ?? []),
          ].forEach((row) =>
            row.label.split("").forEach((digit) => labels.add(digit)),
          );
        } else {
          (line as CartItemDesign).instances.forEach((inst) =>
            inst.logos
              .filter((instLogo) => instLogo.logoId === logoId)
              .forEach((instLogo) =>
                `${instLogo.value ?? ""}`
                  .split("")
                  .forEach((digit) => labels.add(digit)),
              ),
          );
        }
      });
      return Array.from(labels);
    };

    // Merge an extra's quantity into a logo line's label-keyed rows. Name and
    // number logos are ordered through their quantity rows (the scalar
    // toDelivery/toStorage fields are ignored by both the variant expansion
    // and the payload mapper), so extras must land in the rows to survive.
    const mergeExtraIntoRows = (logoLine: CartItemLogo, extra: ExtraLogo) => {
      const rows =
        extra.option === "toStorage"
          ? logoLine.storageQuantities
          : logoLine.deliveryQuantities;
      const labels =
        logoLine.baseLogo.logoType === "NumberLogo"
          ? digitLabelsFor(logoLine.baseLogo.id)
          : [""];
      labels.forEach((label) => addQuantityRow(rows, label, extra.quantity));
    };

    // Clone matched logo lines instead of mutating them: in the "all" branch
    // the standalone logos are still the cart's own objects, and mutating
    // those would bleed dialog quantities back into the cart state.
    const withExtras = orderlines.map((line) => {
      if (line.type !== "Logo") return line;
      const logoLine = line as CartItemLogo;
      const extra = extraLogos.find((e) => e.logo.id.toString() == logoLine.id);
      if (!extra || extra.quantity <= 0) return line;

      const merged: CartItemLogo = {
        ...logoLine,
        toDelivery:
          logoLine.toDelivery +
          (extra.option === "toDelivery" ? extra.quantity : 0),
        toStorage:
          logoLine.toStorage +
          (extra.option === "toStorage" ? extra.quantity : 0),
        deliveryQuantities: (logoLine.deliveryQuantities ?? []).map((q) => ({
          ...q,
        })),
        storageQuantities: (logoLine.storageQuantities ?? []).map((q) => ({
          ...q,
        })),
      };
      if (
        merged.baseLogo.logoType === "NameLogo" ||
        merged.baseLogo.logoType === "NumberLogo"
      ) {
        mergeExtraIntoRows(merged, extra);
      }
      return merged;
    });
    // NOTE: Apart from remvoing empty quantities,
    //  this mess also removes extra logos that are already in the cart,
    //  because their quantity has already been tallied above.
    const extra = extraLogos
      .filter(
        (extra) => !existingLogoIds.has(extra.logo.id) && extra.quantity > 0,
      )
      .map((extraLogo) => {
        const itemLogo = mapBaseLogoToCartItemLogo(
          extraLogo.logo,
          extraLogo.option === "toDelivery" ? extraLogo.quantity : 0,
          extraLogo.option === "toStorage" ? extraLogo.quantity : 0,
        );
        if (extraLogo.logo.logoType === "NumberLogo") {
          // mapBaseLogoToCartItemLogo keys the quantity on an empty label,
          // which the number-logo payload mapper drops - re-key per digit.
          itemLogo.deliveryQuantities = [];
          itemLogo.storageQuantities = [];
          mergeExtraIntoRows(itemLogo, extraLogo);
        }
        return itemLogo;
      });
    return [...withExtras, ...extra];
  }

  function applyLogoVariantLines(orderlines: CartItem[]) {
    const logoOrderLines = orderlines.filter(
      (line) => line.type === "Logo",
    ) as CartItemLogo[];
    const logoLines: CartItemLogo[] = [];
    const nameLogoIds: string[] = [];

    for (const logoLine of logoOrderLines) {
      // Skip NumberLogo processing as it's now handled directly in the mapping logic
      if (
        ["TextLogo", "ImageLogo", "NumberLogo"].includes(logoLine.discriminator)
      )
        continue;

      // Track NameLogo IDs that have variant lines created
      if (logoLine.discriminator === "NameLogo") {
        const deliveries = logoLine.deliveryQuantities ?? [];
        const storages = logoLine.storageQuantities ?? [];

        // Only create variant lines if there are actual quantities with labels
        if (deliveries.length > 0 || storages.length > 0) {
          nameLogoIds.push(logoLine.id);
        }
      }

      const deliveries = logoLine.deliveryQuantities ?? [];
      for (const delivery of deliveries) {
        logoLines.push({
          ...logoLine,
          value: delivery.label,
          toDelivery: delivery.quantity,
          // Ensure we do not create storage lines for logos to delivery.
          toStorage: 0,
        });
      }
      const storages = logoLine.storageQuantities ?? [];
      for (const storage of storages) {
        // Rows with an empty label still carry real quantities (e.g. a name
        // logo instance left blank but sent to storage) - dropping them here
        // would silently lose the quantity from the payload.
        if (storage.label || storage.quantity > 0)
          logoLines.push({
            ...logoLine,
            value: storage.label,
            toStorage: storage.quantity,
            // Ensure we do not create delivery lines for logos to storage.
            toDelivery: 0,
          });
      }
    }

    // Filter out original NameLogo order lines that have variant lines created
    const filteredOrderlines = orderlines.filter((orderline) => {
      if (orderline.type === "Logo") {
        const logoLine = orderline as CartItemLogo;
        return !(
          logoLine.discriminator === "NameLogo" &&
          nameLogoIds.includes(logoLine.id)
        );
      }
      return true;
    });

    return [...filteredOrderlines, ...logoLines];
  }

  function handleDownloadTemplate(
    orgId: number,
    designItems: CartItemDesign[],
  ) {
    startLoading();
    orderRepository
      .downloadTemplate(orgId, designItems)
      .then((blobData) => {
        const url = URL.createObjectURL(blobData);
        const a = document.createElement("a");
        a.href = url;
        a.download = `designs_${designItems
          .map((x) => x.design.product?.mul || x.id)
          .join("_")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((x) => console.log("Error", x))
      .finally(() => {
        stopLoading();
      });
  }

  function handleUploadDesignTemplate(
    orgId: number,
    file: File,
    designId?: number,
  ) {
    async function transaction() {
      return await orderRepository.uploadDesignTemplate(orgId, file, designId);
    }

    startLoading();
    return transaction()
      .then((res) => {
        // Clarity event excel_upload (flag REACT_APP_CLARITY_EVENTS_ENABLED):
        // the Excel template was accepted by the API.
        trackExcelUpload();
        updateDesignWithTemplate(res);
      })
      .finally(stopLoading);
  }

  function handleUploadLogoTemplate(
    orgId: number,
    file: File,
  ): Promise<LogoTemplate> {
    async function transaction() {
      return await orderRepository.uploadLogoTemplate(orgId, file);
    }

    startLoading();
    return transaction()
      .then((res) => {
        trackExcelUpload();
        return res;
      })
      .finally(stopLoading);
  }

  function updateDesignWithTemplate(template: DesignTemplate) {
    const cartItemDesign = designs.find(
      (x) => Number(x.id) === template.designs[0].designId,
    )!;
    const designLogos = cartItemDesign.design.designLogos;
    const placementLabels = getDesignLogoPlacementMap(cartItemDesign.design);

    // TODO Test if instance was just missing.
    const instances = template.designs[0].instances.map((instance) => {
      return {
        label: instance.label,
        toDelivery: instance.quantity,
        toStorage: 0,
        // Bare name lists carry no size - fall back to the design's existing
        // row size (the row the upload dialog was opened from) or M.
        size: instance.size || cartItemDesign.instances[0]?.size || "M",
        logos: instance.logos.map((logo) => {
          const designLogo = designLogos.find((x) => x.logoId === logo.logoId);
          const viewLabel = designLogo
            ? (placementLabels.get(designLogo.inDesignId) ??
              designLogo.viewName?.toUpperCase?.() ??
              "")
            : "";
          const logoVariant = designLogo
            ? getLogoType(designLogo.logo)
            : "name";
          const instanceLogo: CartItemDesignInstanceLogo = {
            value: logo.value,
            logoId: logo.logoId,
            deliveryType: "Delivery",
            locationIdentifier: viewLabel,
            logoVariant: logoVariant,
            designLogoInDesignId: designLogo?.inDesignId,
          };
          return instanceLogo;
        }) as CartItemDesignInstanceLogo[],
      };
    }) as CartItemDesignInstance[];

    updateInCart({ ...cartItemDesign, instances } as CartItemDesign);
  }

  async function handleCommitOrder(
    orgId: number,
    orderId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[],
  ): Promise<Order> {
    async function transaction() {
      // No checkoutItems fallback here on purpose: only the caller knows
      // whether the order being shown is the local checkout draft. A
      // server-fetched draft must never post a stale checkout set.
      const processed = applyLogoVariantLines(orderlines ?? items);
      // 503 navUnavailable (flag navUnavailableEnabled): info toast + ONE
      // retry after retryAfterSeconds (2-15 s) ONLY when the API refused
      // before any NAV call (retrySafe true); flag off = the plain call.
      return await withNavUnavailableRetry(() =>
        orderRepository.commitOrder(
          orgId,
          orderId,
          orderDeliveryPreferences,
          processed,
        ),
      );
    }

    return withToasts(
      transaction,
      t("orderCreate.placingOrderLong"),
      t("orderCreate.orderPlacedSuccess"),
      t("orderCreate.orderPlaceFailed"),
      setIsLoading,
    ).catch(rethrowWithOversizeDialog);
  }

  // Resolves with the full placement ack: ack.checkoutUrl (synchronous Stripe
  // Checkout, server flag) tells the caller to redirect to payment instead of
  // showing the receipt. Applies to act-as placements too — onBehalfOfUserId
  // only attributes the order; the admin doing the placing is the one who pays.
  async function handlePlaceOrder(
    orgId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[],
  ): Promise<PlaceOrderAckDto> {
    async function transaction() {
      // No checkoutItems fallback here on purpose: only the caller knows
      // whether the order being shown is the local checkout draft. A
      // server-fetched draft must never post a stale checkout set.
      const processed = applyLogoVariantLines(orderlines ?? items);
      // Only attach the act-as user when the order actually targets the
      // impersonated org (it always does — orgId flows from the impersonated
      // profile — but never attribute across orgs if that ever changes).
      const onBehalfOfUserId =
        enviroment.actAsVendorEnabled && actAsUser && actAsOrgId === orgId
          ? actAsUser.id
          : undefined;
      // 503 navUnavailable (flag navUnavailableEnabled): see handleCommitOrder.
      return await withNavUnavailableRetry(() =>
        orderRepository.placeOrder(
          orgId,
          orderDeliveryPreferences,
          processed,
          onBehalfOfUserId,
        ),
      );
    }

    return withToasts(
      transaction,
      t("orderCreate.placingOrder"),
      t("orderCreate.orderPlacedSuccess"),
      t("orderCreate.orderPlaceFailed"),
      setIsLoading,
    ).catch(rethrowWithOversizeDialog);
  }

  function mapOrderLinesToOrderItems(orderlines: OrderLine[]): CartItem[] {
    // Unfortunately, TS doesn't recognize this as a non-nullable list, despite the filter at the end.
    // Visual Code appears to be able to understand this, but the language server cannot.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore -- don't remove this line, it's a workaround for a TypeScript bug
    return orderlines
      .filter((line) => !line.relatedTo)
      .map((orderline) => {
        switch (orderline.product.productType) {
          case "Logo": {
            const logo = (orderline as OrderLineLogo).baseLogo;
            const cartItem = mapBaseLogoToCartItemLogo(
              logo,
              (orderline as OrderLineLogo).toDelivery,
              (orderline as OrderLineLogo).toStorage,
            );
            return cartItem;
          }
          case "Design": {
            const design = (orderline as OrderLineDesign).design;
            const instances = mapInstances(orderline as OrderLineDesign);
            const cartItem = mapDesignToCartItemLogo(design, instances);
            return cartItem;
          }
        }
        return null;
      })
      .filter((line) => line != null);
  }

  function mapInstances(orderline: OrderLineDesign): CartItemDesignInstance[] {
    const placementLabels = getDesignLogoPlacementMap(orderline.design);
    const designLogos = orderline.design.designLogos ?? [];
    const instances: CartItemDesignInstance[] = orderline.instances.map((x) => {
      return {
        label: x.label,
        size: x.size,
        toDelivery: x.quantity,
        toStorage: 0,
        casing: orderline.casing,
        logos: x.logos.map((logo) => {
          const designLogo = designLogos.find((d) => d.logoId === logo.logoId);
          const placement = designLogo
            ? (placementLabels.get(designLogo.inDesignId) ??
              designLogo.viewName?.toUpperCase?.() ??
              "")
            : "";
          const cartItem: CartItemDesignInstanceLogo = {
            deliveryType: "Delivery",
            locationIdentifier: placement,
            logoId: logo.logoId,
            logoVariant: logo.isNumeric ? "number" : "name",
            value: logo.value,
            designLogoInDesignId: designLogo?.inDesignId,
          };
          return cartItem;
        }),
      };
    });

    return instances;
  }

  async function handleReorder(order: Order) {
    const lines: OrderLine[] = order.lines;
    const { kept, offending } = splitOversizeCartItems(
      mapOrderLinesToOrderItems(lines),
    );
    if (offending.length > 0) {
      openOversizeSheetDialog(offending, "reorder", oversizeGuard.dialogOptions);
      // Every line refused: nothing goes in the cart and the checkout page
      // is not opened - the dialog is the answer. (An order with no lines at
      // all still navigates as before: offending is empty then.)
      if (kept.length === 0) return;
    }
    return await addItems(kept).then(() => {
      navigate("/order/place");
    });
  }

  async function handleChangeOrder(order: Order) {
    const lines: OrderLine[] = order.lines;
    const { kept, offending } = splitOversizeCartItems(
      mapOrderLinesToOrderItems(lines),
    );
    if (offending.length > 0) {
      openOversizeSheetDialog(offending, "reorder", oversizeGuard.dialogOptions);
      // Every line refused: the current cart is NOT cleared and the order is
      // not moved into it - the dialog is the answer.
      if (kept.length === 0) return [];
    }
    const cartItems = kept;
    // TODO duplicate and set order lines
    await clearItems();
    const items = await addItems(cartItems);
    setActiveOrder({ ...order, lines: [] });
    return items;
  }

  const { getHeatingAndPackaging, getNavisionItemPrice } = useApiClient();

  async function handleChangePackagingOrDelivery(
    order: Order,
    deliveryType: DeliveryType,
    packaging: Packaging,
    deliveryBefore12PM?: boolean,
  ): Promise<Order> {
    setIsLoading(true);

    const isCartForThisOrder = activeOrder?.id === order.id;
    // Rebuild the draft from the checkout-transformed items when available so
    // a packaging/delivery change keeps showing exactly what will be ordered
    // (heating opt-out flattening, extra logos) instead of the raw cart.
    // checkoutItems describe the locally built checkout draft only (id 0) -
    // a server-fetched order must never pick up a stale checkout set.
    const localItems = order.id === 0 ? (checkoutItems ?? items) : items;
    const cartItems =
      isCartForThisOrder && localItems && localItems.length > 0
        ? localItems
        : mapOrderLinesToOrderItems(order.lines);

    let packagingFees: {
      itemNo: string;
      price: number;
      name: string;
      quantity: number;
    }[] = [];

    if (
      packaging === Packaging.StandardBagPackaging ||
      packaging === Packaging.SpecificPackaging
    ) {
      try {
        const heatingAndPackaging = await getHeatingAndPackaging(
          order.organization.id,
        );
        const designs = cartItems
          .filter((x) => x.type === "Design")
          .map((x) => x as CartItemDesign);

        // Group quantities by heating code
        const heatingGroups = new Map<string, number>();

        designs.forEach((d) => {
          const heatingCode = d.design.heating?.sku;
          if (heatingCode) {
            const qty = d.instances.reduce(
              (sum, i) => sum + (i.toDelivery ?? 0),
              0,
            );
            heatingGroups.set(
              heatingCode,
              (heatingGroups.get(heatingCode) ?? 0) + qty,
            );
          }
        });

        if (packaging === Packaging.SpecificPackaging) {
          // Specific packaging: Only one line, quantity 1. Use the first valid item number found.
          // Or find the specific item number associated with any of the heating codes?
          // The requirement implies there's a specific item number per heating code, but we only want ONE line.
          // "we still need to use the product number from the NAV endpoint" -> implies fetching it.
          // If multiple designs have different heating codes, which item number do we pick?
          // "Specific bag packaging is hours-based". Usually this means a single service item.
          // Let's assume we take the first valid mapping we find, or maybe there's a unique specific packaging item?
          // If the mapping returns different specific item numbers for different heating codes, we might have a conflict.
          // But the user says "only add 1 orderline... quantity... 1pcs".
          // Let's grab the first valid itemNo for specific packaging.

          let specificItemNo: string | undefined;
          for (const [heatingCode] of Array.from(heatingGroups.entries())) {
            const mapping = heatingAndPackaging.find(
              (x: any) => x.heatItemNo === heatingCode,
            );
            if (mapping?.specific) {
              specificItemNo = mapping.specific;
              break; // Found one, stop.
            }
          }

          if (specificItemNo) {
            const navItem = await getNavisionItemPrice(
              order.organization.id,
              specificItemNo,
              order.organization.navNumber,
            );
            // Specific packaging usually has a fixed price or base price, but quantity is 1 here.
            // We use the fetched price for 1 unit.
            const priceList = navItem.prices || [];
            const priceObj =
              getProductPrice(priceList, 1) ?? priceList[priceList.length - 1];
            const unitPrice =
              priceObj?.adjustedCustomerPrice ?? priceObj?.unitPrice ?? 0;

            packagingFees.push({
              itemNo: specificItemNo,
              price: unitPrice,
              name: "Specific Packaging", // Hardcoded name
              quantity: 1, // Always 1
            });
          }
        } else {
          // Standard Packaging: Loop through all groups
          for (const [heatingCode, quantity] of Array.from(
            heatingGroups.entries(),
          )) {
            const mapping = heatingAndPackaging.find(
              (x: any) => x.heatItemNo === heatingCode,
            );
            if (mapping) {
              const itemNo = mapping.packingItemNo;
              if (itemNo) {
                const navItem = await getNavisionItemPrice(
                  order.organization.id,
                  itemNo,
                  order.organization.navNumber,
                );
                const priceList = navItem.prices || [];
                const priceObj =
                  getProductPrice(priceList, quantity) ??
                  priceList[priceList.length - 1];
                const unitPrice =
                  priceObj?.adjustedCustomerPrice ?? priceObj?.unitPrice ?? 0;

                let packagingName = "Packaging";
                if (itemNo === "84")
                  packagingName = "Standard packaging (Bags)";
                else if (itemNo === "85")
                  packagingName = "Standard packaging (Jackets)";
                else if (itemNo === "86")
                  packagingName = "Standard packaging (Sweatshirt)";
                else if (itemNo === "87")
                  packagingName = "Standard packaging (Polo shirt)";
                else if (itemNo === "88")
                  packagingName = "Standard packaging (T-shirt)";
                else if (itemNo === "89")
                  packagingName = "Standard packaging (Workwear)";

                packagingFees.push({
                  itemNo: itemNo,
                  price: unitPrice,
                  name: packagingName,
                  quantity: quantity,
                });
              }
            }
          }
        }
      } catch (e) {
        console.error("Error fetching packaging prices", e);
      }
    }

    // Checkout storage verify (flag): the verify answer may have patched the
    // checkout set while the packaging prices were fetched above - build the
    // draft from the CURRENT set so it does not fall back to the counts the
    // notice just corrected. Flag off: the set captured at call start.
    let draftItems = cartItems;
    if (enviroment.checkoutStorageVerifyEnabled && isCartForThisOrder) {
      const latest = latestItemsRef.current;
      const latestLocal =
        order.id === 0 ? (latest.checkoutItems ?? latest.items) : latest.items;
      if (latestLocal && latestLocal.length > 0) draftItems = latestLocal;
    }

    // Same row-derived currency as handlePlaceOrder's draft — see the
    // comment there.
    const draftOrder = buildLocalOrderDraft(
      order.organization.id,
      draftItems,
      getCartItemsCurrency(draftItems, currency),
      organization,
      profile,
      {
        deliveryType,
        packaging,
        deliveryBefore12PM:
          deliveryBefore12PM ?? order.deliveryBefore12PMwithGLS ?? false,
        packagingFees, // Pass pre-calculated fees
      },
    );
    setActiveOrder(draftOrder);
    setIsLoading(false);
    return Promise.resolve(draftOrder);
  }

  // Checkout storage verify (flag checkoutStorageVerifyEnabled): applies the
  // verified storage counts to the LOCAL checkout draft (id 0) and returns
  // the rebuilt draft, or undefined when nothing changed. Patches the
  // checkout items (what the confirmation page shows AND what is posted),
  // the raw cart lines (so "Change" shows the same numbers) and the shared
  // logo/design query cache (cart sync compares the cart against it and
  // would otherwise write the stale count straight back), then rebuilds the
  // draft along the same path as handleCreateOrder - heating marking,
  // variant expansion, buildLocalOrderDraft - with the order's CURRENT
  // delivery type, packaging and packaging fee lines, so no price/date call
  // is repeated. latestDelivery is carried over on purpose: the confirmation
  // page's delivery-date effect keys on it, and the products did not change.
  function handleApplyVerifiedStorage(
    order: Order,
    rows: VerifyStorageRowDto[],
  ): Order | undefined {
    if (order.id !== 0) return undefined;
    const source = checkoutItems ?? items;
    const patchedCheckout = patchStorageCounts(source, rows);
    if (patchedCheckout === source) return undefined;
    setCheckoutItems(patchedCheckout);

    const patchedCart = patchStorageCounts(items, rows);
    if (patchedCart !== items) {
      patchedCart.forEach((item, index) => {
        if (item !== items[index]) {
          updateInCart(item).catch(() => undefined);
        }
      });
    }
    syncStorageQueryCache(patchedCart);

    const draftOrderLines = markHeatingDeliveryTypeForDraft(
      patchedCheckout,
      heatingOption,
    );
    const allLogoOrderLines = applyLogoVariantLines(draftOrderLines);
    const draftOrder = buildLocalOrderDraft(
      order.organization.id,
      allLogoOrderLines,
      getCartItemsCurrency(allLogoOrderLines, currency),
      organization,
      profile,
      {
        deliveryType: order.deliveryType,
        packaging: order.packagingType,
        deliveryBefore12PM: order.deliveryBefore12PMwithGLS ?? false,
        packagingFees: packagingFeesFromOrder(order),
      },
    );
    const rebuilt: Order = { ...draftOrder, latestDelivery: order.latestDelivery };
    setActiveOrder(rebuilt);
    return rebuilt;
  }

  // "Prices on their way" (flag productPendingEnabled): once the worker has
  // landed a product, cart sync refreshes the LIVE cart - but the LOCAL
  // checkout draft (id 0) shows and posts the frozen checkout snapshot,
  // whose lines still carry the pre-worker baseLogo/design (old or missing
  // prices). Swaps the settled products from the live cart into the
  // snapshot's pending lines and rebuilds the draft along the same path as
  // handleApplyVerifiedStorage (heating marking, variant expansion,
  // buildLocalOrderDraft with the order's CURRENT delivery/packaging/fees;
  // latestDelivery carried over). Returns the rebuilt draft, or undefined
  // when nothing was pending or the live cart has nothing fresh yet. Flag
  // off = always undefined.
  function handleApplyPendingProducts(order: Order): Order | undefined {
    if (!enviroment.productPendingEnabled) return undefined;
    if (order.id !== 0) return undefined;
    const latest = latestItemsRef.current;
    const source = latest.checkoutItems ?? latest.items;
    const patchedCheckout = patchPendingProducts(source, latest.items);
    if (patchedCheckout === source) return undefined;
    setCheckoutItems(patchedCheckout);

    const draftOrderLines = markHeatingDeliveryTypeForDraft(
      patchedCheckout,
      heatingOption,
    );
    const allLogoOrderLines = applyLogoVariantLines(draftOrderLines);
    const draftOrder = buildLocalOrderDraft(
      order.organization.id,
      allLogoOrderLines,
      getCartItemsCurrency(allLogoOrderLines, currency),
      organization,
      profile,
      {
        deliveryType: order.deliveryType,
        packaging: order.packagingType,
        deliveryBefore12PM: order.deliveryBefore12PMwithGLS ?? false,
        packagingFees: packagingFeesFromOrder(order),
      },
    );
    const rebuilt: Order = { ...draftOrder, latestDelivery: order.latestDelivery };
    setActiveOrder(rebuilt);
    return rebuilt;
  }

  // Same key shapes as useCartSync / LogoRepository.useLogo /
  // DesignRepository.useDesign (["logos"|"designs", actAs, id]). Only
  // entries that exist are touched; the updater returning undefined leaves
  // an absent entry absent - and a logo detail entry that is still the
  // popup's list-row stub untouched (storageFieldsUpdater).
  function syncStorageQueryCache(patchedItems: CartItem[]) {
    const actAsKey = actAsCacheKey();
    patchedItems.forEach((item) => {
      if (item.type === "Logo") {
        const fresh = (item as CartItemLogo).baseLogo;
        if (!fresh?.id) return;
        queryClient.setQueryData<BaseLogo | undefined>(
          ["logos", actAsKey, fresh.id],
          storageFieldsUpdater(fresh),
        );
      } else if (item.type === "Design") {
        const design = (item as CartItemDesign).design;
        if (!design?.id) return;
        const freshLogos = new Map<number, BaseLogo>();
        design.designLogos?.forEach((dl) => {
          if (dl.logo) freshLogos.set(dl.logoId ?? dl.logo.id, dl.logo);
        });
        queryClient.setQueryData<Design | undefined>(
          ["designs", actAsKey, design.id],
          (cached) =>
            cached
              ? {
                  ...cached,
                  designLogos: (cached.designLogos ?? []).map((dl) => {
                    const fresh = freshLogos.get(dl.logoId ?? dl.logo?.id);
                    return fresh && dl.logo
                      ? {
                          ...dl,
                          logo: {
                            ...dl.logo,
                            inStorage: fresh.inStorage,
                            numberLogosInStorage: fresh.numberLogosInStorage,
                            storageUpdatedAtUtc: fresh.storageUpdatedAtUtc,
                          },
                        }
                      : dl;
                  }),
                }
              : undefined,
        );
      }
    });
  }

  return {
    isLoading,
    handleLogoAddOrderLine,
    handleLogoAddOrderLines,
    handleDesignAddOrderLine,
    handleDesignAddOrderLines,
    handleRemoveOrderLine,
    handleLogoSetQuantities,
    updateLogoOrderLine,
    handleDesignInstanceUpdateStorage,
    handlePlaceVerdict,
    handleCreateOrder,
    handleDownloadTemplate,
    handleUploadDesignTemplate,
    handleUploadLogoTemplate,
    handleCommitOrder,
    handlePlaceOrder,
    handleChangeOrder,
    handleChangePackagingOrDelivery,
    handleApplyVerifiedStorage,
    handleApplyPendingProducts,
    handleReorder,
  };
}

// Local lightweight draft builder to mirror OrderDto enough for the confirmation page without a backend roundtrip.
function buildLocalOrderDraft(
  orgId: number,
  orderItems: CartItem[],
  currency: string,
  organization?: Organization,
  profile?: Profile,
  options?: {
    deliveryType?: DeliveryType;
    packaging?: Packaging;
    deliveryBefore12PM?: boolean;
    packagingFees?: {
      itemNo: string;
      price: number;
      name: string;
      quantity: number;
    }[];
  },
): Order {
  let nextId = 1;
  const lines: OrderLine[] = [];

  const isCustomer = profile?.userOrganizationInformations?.role === 0; // Role.EndUser is 0

  const getApplicablePrice = (prices: Price[], quantity: number): number => {
    if (!prices || prices.length === 0) return 0;
    const priceObj = getProductPrice(prices, quantity);

    if (!priceObj) return 0;

    return isCustomer &&
      priceObj.adjustedCustomerPrice !== undefined &&
      priceObj.adjustedCustomerPrice !== null &&
      priceObj.adjustedCustomerPrice > 0
      ? priceObj.adjustedCustomerPrice
      : priceObj.adjustedPrice;
  };

  const createAdditionalFeeLines = (
    baseLogoLineId: number,
    baseLogo: BaseLogo,
    baseQuantity: number,
  ): OrderLine[] => {
    const feeLines: OrderLine[] = [];
    const fees = baseLogo.product?.additionalFees ?? [];

    fees.forEach((fee) => {
      let feeTotal = 0;
      let unitPrice = fee.unitPrice ?? 0;
      let numberOfColors = null;
      if (
        fee.name.toLowerCase().includes("startup") ||
        fee.name.toLowerCase().includes("start-up") ||
        fee.name.toLowerCase().includes("opstart") ||
        fee.name.toLowerCase().includes("film")
      ) {
        const perColor = getApplicablePrice(fee.prices ?? [], baseQuantity);
        const type = baseLogo.transferMethod?.type?.toLowerCase() ?? "";
        const isEmbroidery = type === "broderi" || type === "embroidery";

        numberOfColors = isEmbroidery ? 1 : baseLogo.colors?.length || 1;
        unitPrice = perColor;
        feeTotal = perColor * numberOfColors;
      } else {
        // For non-startup fees, we also need to respect customer pricing
        // Assuming fee.prices exists for these fees too
        if (fee.prices && fee.prices.length > 0) {
          const price = getApplicablePrice(fee.prices, baseQuantity);
          unitPrice = price;
        } else {
          // Fallback if prices array is missing (though it should be there)
          unitPrice = fee.unitPrice ?? 0;
        }
        feeTotal = unitPrice * (fee.quantity ?? 1);
      }

      if (unitPrice > 0) {
        feeLines.push({
          id: nextId++,
          displayNo: "",
          type: "Additional Fee",
          price: unitPrice,
          unitPrice: unitPrice,
          adjustedPrice: unitPrice,
          adjustedUnitPrice: unitPrice,
          quantity: numberOfColors ?? fee.quantity,
          title: fee.name,
          toStorage: 0,
          toDelivery: 0,
          relatedTo: baseLogoLineId,
          product: {
            productNo: fee.itemNo,
            productType: "AdditionalFee",
            productName: fee.name,
          },
          deliveryType: "Delivery",
          additionalFees: [],
        });
      }
    });
    return feeLines;
  };

  // Group name/number logos by SKU to avoid duplicate parents when variant lines were already expanded.
  const nameNumberGroups: Record<
    string,
    {
      logo: CartItemLogo;
      deliveries: QuantityRow[];
      storages: QuantityRow[];
      values: {
        label: string | number | undefined;
        toDelivery: number;
        toStorage: number;
      }[];
    }
  > = {};

  const normalItems: CartItem[] = [];
  const processedDeliveryArrays = new Set<QuantityRow[]>();
  const processedStorageArrays = new Set<QuantityRow[]>();

  // Global storage tracking across all items
  const globalUsedStorage = new Map<string, number>();

  const getGlobalAvailableStorage = (baseLogo: BaseLogo, digit?: string) => {
    let total = 0;
    let key = "";

    if (baseLogo.logoType === "NumberLogo" && digit) {
      total =
        baseLogo.numberLogosInStorage?.find((x) => x.value === digit)
          ?.inStorage ?? 0;
      key = `${baseLogo.id}::${digit}`;
    } else {
      total = baseLogo.inStorage ?? 0;
      key = `${baseLogo.id}`;
    }

    const used = globalUsedStorage.get(key) ?? 0;
    return Math.max(0, total - used);
  };

  const consumeGlobalStorage = (
    baseLogo: BaseLogo,
    amount: number,
    digit?: string,
  ) => {
    const key =
      baseLogo.logoType === "NumberLogo" && digit
        ? `${baseLogo.id}::${digit}`
        : `${baseLogo.id}`;
    const used = globalUsedStorage.get(key) ?? 0;
    globalUsedStorage.set(key, used + amount);
  };

  // Pre-calculate global total quantities for each logo to determine correct price tier
  const globalLogoTotalQuantities = new Map<number, number>();

  // Track global net production for fees (to avoid duplicate fees)
  const globalFeeData = new Map<
    number,
    { netQty: number; parentLineId?: number; baseLogo: BaseLogo }
  >();

  // Shared storage allocator for the price-tier pre-pass. Tracks remaining
  // inStorage as we walk through every cart item / design instance so that
  // when multiple instances or cart items share a logo, storage is consumed
  // exactly once across them. Kept separate from globalUsedStorage (which
  // runs during line emission) to avoid double-consumption.
  const tierRemainingStorage = new Map<string, number>();
  const storageKey = (baseLogo: BaseLogo, digit?: string) =>
    baseLogo.logoType === "NumberLogo" && digit
      ? `${baseLogo.id}::${digit}`
      : `${baseLogo.id}`;
  // Negative stored values (e.g., over-consumed historic storage) are
  // treated as 0 so the price-tier pre-pass can't inflate payable quantities
  // by subtracting a negative "taken" amount from toDelivery.
  const initialStorageFor = (baseLogo: BaseLogo, digit?: string): number => {
    const raw =
      baseLogo.logoType === "NumberLogo" && digit
        ? (baseLogo.numberLogosInStorage?.find((x) => x.value === digit)
            ?.inStorage ?? 0)
        : (baseLogo.inStorage ?? 0);
    return Math.max(0, raw);
  };
  const takeFromTierStorage = (
    baseLogo: BaseLogo,
    amount: number,
    digit?: string,
  ): number => {
    const key = storageKey(baseLogo, digit);
    if (!tierRemainingStorage.has(key)) {
      tierRemainingStorage.set(key, initialStorageFor(baseLogo, digit));
    }
    const remaining = tierRemainingStorage.get(key) ?? 0;
    const taken = Math.min(amount, remaining);
    tierRemainingStorage.set(key, remaining - taken);
    return taken;
  };

  // Compute how much of a logo placement is actually paid for (delivery beyond
  // existing storage, plus any new toStorage). Mirrors the place-order page's
  // payableQuantity formula. The returned amount is in tier units (digit
  // applications for NumberLogos, pieces otherwise) so it can be added
  // directly to globalLogoTotalQuantities without the digit multiplier.
  const consumePayable = (
    baseLogo: BaseLogo,
    toDelivery: number,
    toStorage: number,
    value: string | number | undefined,
  ): number => {
    if (baseLogo.logoType === "NumberLogo" && value !== undefined) {
      const digits = value.toString().split("");
      let payable = 0;
      for (const digit of digits) {
        const taken = takeFromTierStorage(baseLogo, toDelivery, digit);
        payable +=
          Math.max(0, toDelivery - taken) + Math.max(0, toStorage);
      }
      return payable;
    }
    const taken = takeFromTierStorage(baseLogo, toDelivery);
    return Math.max(0, toDelivery - taken) + Math.max(0, toStorage);
  };

  const addPayableToGlobalQty = (baseLogo: BaseLogo, payable: number) => {
    if (payable <= 0) return;
    const current = globalLogoTotalQuantities.get(baseLogo.id) ?? 0;
    globalLogoTotalQuantities.set(baseLogo.id, current + payable);
  };

  for (const item of orderItems) {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;
      const toDelivery = logoItem.toDelivery ?? 0;
      const toStorage = logoItem.toStorage ?? 0;
      if (toDelivery + toStorage <= 0) continue;
      const payable = consumePayable(
        logoItem.baseLogo,
        toDelivery,
        toStorage,
        logoItem.value,
      );
      addPayableToGlobalQty(logoItem.baseLogo, payable);
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;
      designItem.instances.forEach((inst) => {
        const instDelivery = inst.toDelivery ?? 0;
        const instStorage = inst.toStorage ?? 0;
        if (instDelivery + instStorage <= 0) return;
        inst.logos.forEach((l) => {
          const designLogo = designItem.design.designLogos?.find(
            (d) => d.logoId === l.logoId,
          );
          const baseLogo = designLogo?.logo ?? l.baseLogo;
          if (!baseLogo) return;
          const payable = consumePayable(
            baseLogo,
            instDelivery,
            instStorage,
            l.value,
          );
          addPayableToGlobalQty(baseLogo, payable);
        });
      });
    }
  }

  for (const item of orderItems) {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;
      const isNumberLogo = logoItem.baseLogo.logoType === "NumberLogo";
      const isNameLogo = logoItem.baseLogo.logoType === "NameLogo";
      const sku = logoItem.baseLogo.product?.sku ?? logoItem.id;

      if (isNumberLogo || isNameLogo) {
        if (!nameNumberGroups[sku]) {
          nameNumberGroups[sku] = {
            logo: logoItem,
            deliveries: [],
            storages: [],
            values: [],
          };
        }
        const group = nameNumberGroups[sku];

        const deliveryRows = logoItem.deliveryQuantities ?? [];
        if (
          deliveryRows.length > 0 &&
          !processedDeliveryArrays.has(deliveryRows)
        ) {
          group.deliveries.push(...deliveryRows);
          processedDeliveryArrays.add(deliveryRows);
        }

        const storageRows = logoItem.storageQuantities ?? [];
        if (
          storageRows.length > 0 &&
          !processedStorageArrays.has(storageRows)
        ) {
          group.storages.push(...storageRows);
          processedStorageArrays.add(storageRows);
        }
        // If there are no rows but a value/toDelivery is present, treat as value row.
        if (
          (logoItem.deliveryQuantities?.length ?? 0) === 0 &&
          (logoItem.storageQuantities?.length ?? 0) === 0 &&
          (logoItem.toDelivery ?? 0) + (logoItem.toStorage ?? 0) > 0
        ) {
          group.values.push({
            label: logoItem.value,
            toDelivery: logoItem.toDelivery ?? 0,
            toStorage: logoItem.toStorage ?? 0,
          });
        }
      } else {
        normalItems.push(item);
      }
    } else {
      normalItems.push(item);
    }
  }

  // Process normal items first
  for (const item of normalItems) {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;

      // Use top-level properties as source of truth for normal items to avoid stale row data
      const deliveryQty = logoItem.toDelivery ?? 0;
      const storageQty = logoItem.toStorage ?? 0;

      // Check if this is a "To Storage" ONLY order (no delivery, positive storage)
      const isOnlyStorage = deliveryQty === 0 && storageQty > 0;

      // Parent line with image/title, now carries the delivery price/quantity.
      // If "Only Storage", parent carries the storage quantity.
      const parentQty = isOnlyStorage ? storageQty : deliveryQty;
      const parentLineId = nextId++;

      // If only storage, we use the storage price (unit price * 1, positive)
      // Wait, standard logic for "To Storage" line is:
      // price = baseUnitPrice * multiplier (where multiplier is 1)
      // productNo = "To Storage"
      // deliveryType = "Storage"
      // toStorage = qty
      // toDelivery = 0

      // Calculate price based on the correct quantity (global total)
      const baseUnitPrice = getApplicablePrice(
        logoItem.baseLogo.product?.prices ?? [],
        globalLogoTotalQuantities.get(logoItem.baseLogo.id) ?? parentQty,
      );

      const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

      lines.push({
        id: parentLineId,
        displayNo: "",
        type: "Logo",
        price: baseUnitPrice,
        unitPrice: baseUnitPrice,
        adjustedPrice: baseUnitPrice,
        adjustedUnitPrice: baseUnitPrice,
        quantity: parentQty,
        title: logoTitle,
        displayImage: logoItem.displayImage?.lowResUrl ?? "",
        toStorage: isOnlyStorage ? parentQty : (logoItem.toStorage ?? 0),
        toDelivery: isOnlyStorage ? 0 : (logoItem.toDelivery ?? 0),
        relatedTo: 0,
        product: {
          productNo: isOnlyStorage
            ? "To Storage"
            : (logoItem.baseLogo.product?.sku ?? ""),
          productSku: logoItem.baseLogo.product?.sku,
          productType: "Logo",
          productName: logoTitle,
        },
        deliveryType: isOnlyStorage ? "Storage" : "Delivery",
        additionalFees: [],
        baseLogo: logoItem.baseLogo,
      } as OrderLineLogo);

      // We need to track the first emitted line ID to attach fees to it later
      // Since the main line is now the delivery line (or storage line), we use its ID.
      let deliveryLineId: number | undefined = parentLineId;

      const emitLine = (
        label: string | number | undefined,
        qty: number,
        type: "Delivery" | "ToStorage" | "FromStorage",
        priceMultiplier: number = 1,
      ) => {
        // Skip redundant delivery sub-line
        if (type === "Delivery" && !isOnlyStorage) return;
        // Skip redundant storage sub-line if it's "Only Storage"
        if (type === "ToStorage" && isOnlyStorage) return;

        const multiplier = priceMultiplier;
        if (qty <= 0) return;

        const isFromStorage = type === "FromStorage";
        const isToStorage = type === "ToStorage";

        const baseUnitPrice = getApplicablePrice(
          logoItem.baseLogo.product?.prices ?? [],
          globalLogoTotalQuantities.get(logoItem.baseLogo.id) ?? qty,
        );

        // From Storage has negative price
        const unitPrice = isFromStorage
          ? baseUnitPrice * multiplier * -1
          : baseUnitPrice * multiplier;

        const lineId = nextId++;

        // SKU logic: "From Storage", "To Storage", or original
        let productNo = logoItem.baseLogo.product?.sku ?? "";
        if (isFromStorage) productNo = "From Storage";
        else if (isToStorage) productNo = "To Storage";

        const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

        const baseTitle =
          label !== undefined && label !== ""
            ? `${logoTitle} (${label})`
            : logoTitle;
        // No suffix in title as requested
        const title = baseTitle;

        // If From Storage, we want it related to the Delivery line if it exists
        // Otherwise fallback to parentLineId (shouldn't happen in valid flow, but safe fallback)
        const relatedToId =
          isFromStorage && deliveryLineId ? deliveryLineId : parentLineId;

        lines.push({
          id: lineId,
          displayNo: "",
          type: "Logo",
          price: unitPrice,
          unitPrice: unitPrice,
          adjustedPrice: unitPrice,
          adjustedUnitPrice: unitPrice,
          quantity: qty,
          title: title,
          displayImage: undefined,
          toStorage: isToStorage ? qty : 0,
          toDelivery: !isToStorage ? qty : 0,
          relatedTo: relatedToId,
          product: {
            productNo: productNo,
            productSku: logoItem.baseLogo.product?.sku,
            productType: "Logo",
            productName: title,
          },
          deliveryType: isToStorage ? "Storage" : "Delivery",
          additionalFees: [],
          baseLogo: logoItem.baseLogo,
        } as OrderLineLogo);

        // Fees are now handled once after all lines are emitted
      };

      // 1. To Delivery (Full amount) - Skip if Only Storage
      if (!isOnlyStorage) {
        emitLine(undefined, deliveryQty, "Delivery");
      }

      // 2. To Storage (Full amount) - Skip if Only Storage (already parent)
      emitLine(undefined, storageQty, "ToStorage");

      // 3. From Storage (Offset) - Only relevant if not Only Storage (since Only Storage implies no delivery)
      let fromStorage = 0;
      if (!isOnlyStorage) {
        const availableStorage = getGlobalAvailableStorage(logoItem.baseLogo);
        fromStorage = Math.min(deliveryQty, availableStorage);
        consumeGlobalStorage(logoItem.baseLogo, fromStorage);

        emitLine(undefined, fromStorage, "FromStorage");
      }

      // 4. Fees (Once, based on net production)
      // Attach fees to the main delivery line if it exists, otherwise the first available line (e.g. storage)
      const feeParentId = deliveryLineId ?? lines[lines.length - 1]?.id;

      const netProductionQty = deliveryQty - fromStorage + storageQty;
      // Only charge fees if we are actually producing new logos
      // i.e., netProductionQty > 0
      // However, the user specifically requested:
      // "if a logo is ordering only 'From Storage', and the quantity to delivery is >= than the quantity in storage, and there's no 'To Storage', then we should not include any additional fees."
      // This implies: if (deliveryQty <= fromStorage && storageQty === 0) -> No fees.
      // Which is covered by netProductionQty > 0 check, because:
      // if deliveryQty <= fromStorage, then deliveryQty - fromStorage <= 0.
      // if storageQty == 0, then netProductionQty <= 0.
      // So the existing check `netProductionQty > 0` already covers this?
      // Wait, fromStorage = Math.min(deliveryQty, availableStorage).
      // So if availableStorage >= deliveryQty, then fromStorage = deliveryQty.
      // Then deliveryQty - fromStorage = 0.
      // If storageQty = 0, then netProductionQty = 0.
      // So yes, netProductionQty > 0 covers "Only charge fees if we are producing new logos".

      if (netProductionQty > 0) {
        const current = globalFeeData.get(logoItem.baseLogo.id) ?? {
          netQty: 0,
          baseLogo: logoItem.baseLogo,
        };
        current.netQty += netProductionQty;
        if (!current.parentLineId && feeParentId !== undefined) {
          current.parentLineId = feeParentId;
        }
        globalFeeData.set(logoItem.baseLogo.id, current);
      }
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;
      const quantity = designItem.instances.reduce(
        (sum, inst) => sum + (inst?.toDelivery ?? 0) + (inst?.toStorage ?? 0),
        0,
      );
      // Main design line has 0 price, sub-items carry the price
      const unitPrice = 0;

      const lineId = nextId++;
      lines.push({
        id: lineId,
        displayNo: "",
        type: "Design",
        price: unitPrice,
        unitPrice: unitPrice,
        adjustedPrice: unitPrice,
        adjustedUnitPrice: unitPrice,
        quantity: quantity,
        title: designItem.title,
        displayImage: designItem.displayImage?.lowResUrl ?? "",
        toStorage: 0,
        toDelivery: quantity,
        relatedTo: 0,
        product: {
          productNo: designItem.design.product?.mul ?? "",
          productMul: designItem.design.product?.mul,
          productType: "Design",
          productName: designItem.title,
        },
        deliveryType: "Delivery",
        additionalFees: [],
        design: designItem.design,
        instances: designItem.instances.map((inst) => ({
          label: inst.label,
          quantity: inst.toDelivery,
          size: inst.size,
          logos: inst.logos.map((l) => ({
            value: l.value,
            isNumeric: l.logoVariant === "number",
            logoId: l.logoId,
          })),
        })),
      } as OrderLineDesign);

      // Heating line (related to design)
      if (designItem.design.heating) {
        const heatingQty = designItem.instances.reduce((total, inst) => {
          const instQty = (inst.toDelivery ?? 0) + (inst.toStorage ?? 0);
          if (instQty <= 0) return total;

          const instanceMultiplier = inst.logos.filter((l) => {
            const designLogo = designItem.design.designLogos?.find(
              (d) => d.logoId === l.logoId,
            );
            const baseLogo = designLogo?.logo ?? l.baseLogo;
            const type = baseLogo?.transferMethod?.type?.toLowerCase() ?? "";
            return (
              type !== "broderi" &&
              type !== "embroidery" &&
              type !== "direk tryk" &&
              type !== "direkte tryk"
            );
          }).length;

          return total + instQty * instanceMultiplier;
        }, 0);

        if (heatingQty > 0) {
          const heatingUnitPrice = getApplicablePrice(
            designItem.design.heating.prices ?? [],
            heatingQty,
          );

          lines.push({
            id: nextId++,
            displayNo: "",
            type: "Heating",
            price: heatingUnitPrice,
            unitPrice: heatingUnitPrice,
            adjustedPrice: heatingUnitPrice,
            adjustedUnitPrice: heatingUnitPrice,
            quantity: heatingQty,
            title: designItem.design.heating.name,
            displayImage: designItem.displayImage?.lowResUrl ?? "",
            toStorage: 0,
            toDelivery: heatingQty,
            relatedTo: lineId,
            product: {
              productNo: designItem.design.heating.sku,
              productSku: designItem.design.heating.sku,
              productType: "Heating",
              productName: designItem.design.heating.name,
            },
            deliveryType: "Delivery",
            additionalFees: [],
          } as OrderLine);
        }
      }

      // Create child logo lines: Aggregate quantities for static logos across all instances
      // Variable logos (Names/Numbers) are kept separate per value
      const logoAggregator = new Map<
        string,
        {
          quantity: number;
          logoSpec: CartItemDesignInstanceLogo;
          baseLogo?: BaseLogo;
        }
      >();

      designItem.instances.forEach((inst) => {
        inst.logos.forEach((logoSpec) => {
          const designLogo =
            designItem.design.designLogos?.find(
              (d) => d.logoId === logoSpec.logoId,
            ) ?? undefined;

          // Prefer designLogo.logo (fresh from backend) to ensure latest inStorage count
          const baseLogo = designLogo?.logo ?? logoSpec.baseLogo;
          const isNumberLogo = baseLogo?.logoType === "NumberLogo";
          const isNameLogo = baseLogo?.logoType === "NameLogo";

          // For static logos, key by logoId only (merges all instances).
          // For variable logos, key by logoId + value.
          let key = `${logoSpec.logoId}`;
          if (isNumberLogo || isNameLogo) {
            key += `::${logoSpec.value ?? ""}`;
          }

          if (!logoAggregator.has(key)) {
            logoAggregator.set(key, {
              quantity: 0,
              logoSpec,
              baseLogo,
            });
          }
          const entry = logoAggregator.get(key)!;
          // Accumulate delivery quantity
          entry.quantity += inst.toDelivery ?? 0;
        });
      });

      const processedFeeLogos = new Set<number>();
      const totalQuantitiesPerLogo = new Map<number, number>();
      const totalFromStoragePerLogo = new Map<number, number>();

      // Pre-calculate total effective quantities per logo (including digit multipliers)
      const feeData = new Map<number, { lineId: number; baseLogo: BaseLogo }>();

      logoAggregator.forEach(({ quantity, logoSpec, baseLogo }) => {
        if (!baseLogo) return;
        const isNumberLogo = baseLogo.logoType === "NumberLogo";
        let multiplier = 1;
        if (isNumberLogo) {
          const digits = `${logoSpec.value ?? ""}`;
          multiplier = digits.length || 1;
        }
        const current = totalQuantitiesPerLogo.get(baseLogo.id) ?? 0;
        totalQuantitiesPerLogo.set(
          baseLogo.id,
          current + quantity * multiplier,
        );
      });

      logoAggregator.forEach(({ quantity, logoSpec, baseLogo }) => {
        if (quantity <= 0) return;

        const emitInstanceLogoLine = (
          displayVal: string | number | undefined,
          qty: number,
          priceMultiplier: number = 1,
        ) => {
          const baseLogoTitle = baseLogo
            ? formatLogoTitleWithSize(baseLogo.title, baseLogo)
            : designItem.title;

          const logoQty = qty;
          const baseUnitPrice =
            baseLogo && baseLogo.product
              ? getApplicablePrice(
                  baseLogo.product.prices ?? [],
                  globalLogoTotalQuantities.get(baseLogo.id) ?? logoQty,
                )
              : 0;
          const logoUnitPrice = baseUnitPrice;

          const logoLineId = nextId++;
          lines.push({
            id: logoLineId,
            displayNo: "",
            type: "Logo",
            price: logoUnitPrice,
            adjustedPrice: logoUnitPrice,
            adjustedUnitPrice: logoUnitPrice,
            unitPrice: logoUnitPrice,
            quantity: logoQty * priceMultiplier,
            title:
              displayVal !== undefined && displayVal !== ""
                ? `${baseLogoTitle} (${displayVal})`
                : baseLogoTitle,
            displayImage: baseLogo?.displayImage?.lowResUrl ?? "",
            toStorage: 0,
            toDelivery: logoQty,
            relatedTo: lineId,
            product: {
              productNo: baseLogo?.product?.sku ?? "",
              productSku: baseLogo?.product?.sku,
              productType: "Logo",
              productName:
                displayVal !== undefined && displayVal !== ""
                  ? `${baseLogoTitle} (${displayVal})`
                  : baseLogoTitle,
            },
            deliveryType:
              logoSpec.deliveryType === "Application"
                ? "Application"
                : "Delivery",
            additionalFees: [],
            baseLogo: baseLogo as BaseLogo,
          } as OrderLineLogo);

          if (baseLogo) {
            // Check storage availability
            let fromStorageQty = 0;
            const isNumberLogo = baseLogo.logoType === "NumberLogo";

            if (isNumberLogo) {
              const digits = displayVal?.toString().split("") ?? [];
              digits.forEach((digit) => {
                const avail = getGlobalAvailableStorage(baseLogo, digit);
                const take = Math.min(qty, avail); // qty is number of garments
                consumeGlobalStorage(baseLogo, take, digit);
                fromStorageQty += take;
              });
            } else {
              const avail = getGlobalAvailableStorage(baseLogo);
              const take = Math.min(qty, avail);
              consumeGlobalStorage(baseLogo, take);
              fromStorageQty = take;
            }

            if (fromStorageQty > 0) {
              const fromStoragePrice = baseUnitPrice * -1;
              const fromStorageLineId = nextId++;
              lines.push({
                id: fromStorageLineId,
                displayNo: "",
                type: "Logo",
                price: fromStoragePrice,
                unitPrice: fromStoragePrice,
                adjustedPrice: fromStoragePrice,
                adjustedUnitPrice: fromStoragePrice,
                quantity: fromStorageQty,
                title: baseLogoTitle,
                displayImage: undefined,
                toStorage: 0,
                toDelivery: fromStorageQty,
                relatedTo: logoLineId,
                product: {
                  productNo: "From Storage",
                  productSku: baseLogo.product?.sku,
                  productType: "Logo",
                  productName: baseLogoTitle,
                },
                deliveryType:
                  logoSpec.deliveryType === "Application"
                    ? "Application"
                    : "Delivery",
                additionalFees: [],
                baseLogo: baseLogo,
              } as OrderLineLogo);
            }

            // Track total from storage for fee calculation
            const currentFromStorage =
              totalFromStoragePerLogo.get(baseLogo.id) ?? 0;
            totalFromStoragePerLogo.set(
              baseLogo.id,
              currentFromStorage + fromStorageQty,
            );

            // Store fee data for later processing
            if (!feeData.has(baseLogo.id)) {
              feeData.set(baseLogo.id, {
                lineId: logoLineId,
                baseLogo: baseLogo,
              });
            }
          }
        };

        const isNumberLogo = baseLogo?.logoType === "NumberLogo";
        const isNameLogo = baseLogo?.logoType === "NameLogo";

        if (isNumberLogo) {
          const digits = `${logoSpec.value ?? ""}`;
          const priceMultiplier = digits.length || 1;
          emitInstanceLogoLine(digits, quantity, priceMultiplier);
        } else if (isNameLogo) {
          emitInstanceLogoLine(logoSpec.value, quantity, 1);
        } else {
          emitInstanceLogoLine(undefined, quantity, 1);
        }
      });

      // Process fees after all lines are emitted
      feeData.forEach(({ lineId, baseLogo }, baseLogoId) => {
        const totalQty = totalQuantitiesPerLogo.get(baseLogoId) ?? 0;
        const totalFromStorage = totalFromStoragePerLogo.get(baseLogoId) ?? 0;
        const netQty = totalQty - totalFromStorage;

        if (netQty > 0) {
          const current = globalFeeData.get(baseLogo.id) ?? {
            netQty: 0,
            baseLogo: baseLogo,
          };
          current.netQty += netQty;
          if (!current.parentLineId) {
            current.parentLineId = lineId;
          }
          globalFeeData.set(baseLogo.id, current);
        }
      });
    }
  }

  // Process grouped name/number logos (one parent per SKU)
  for (const sku of Object.keys(nameNumberGroups)) {
    const group = nameNumberGroups[sku];
    const logoItem = group.logo;

    const rowsDeliveries = group.deliveries;
    const rowsStorages = group.storages;
    const isGroupNumberLogo = logoItem.baseLogo.logoType === "NumberLogo";

    // Aggregate rows by label to avoid duplicates
    const aggregateRows = (rows: QuantityRow[]) => {
      const map = new Map<string, number>();
      rows.forEach((r) => {
        const key = `${r.label ?? ""}`;
        map.set(key, (map.get(key) ?? 0) + r.quantity);
      });
      return Array.from(map.entries()).map(([label, quantity]) => ({
        label,
        quantity,
      }));
    };

    let aggDeliveries = aggregateRows(rowsDeliveries);
    let aggStorages = aggregateRows(rowsStorages);

    // Fallback to values if no rows exist
    if (
      aggDeliveries.length === 0 &&
      aggStorages.length === 0 &&
      group.values.length > 0
    ) {
      const deliveryValues = group.values
        .filter((v) => v.toDelivery > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toDelivery,
        }));
      const storageValues = group.values
        .filter((v) => v.toStorage > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toStorage,
        }));

      aggDeliveries = aggregateRows(deliveryValues);
      aggStorages = aggregateRows(storageValues);
    } else if (group.values.length > 0) {
      // Merge values into existing aggregations if they exist but were not captured in rows
      const deliveryValues = group.values
        .filter((v) => v.toDelivery > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toDelivery,
        }));
      const storageValues = group.values
        .filter((v) => v.toStorage > 0)
        .map((v) => ({
          label: `${v.label ?? ""}`,
          quantity: v.toStorage,
        }));

      // Merge helper
      const merge = (
        existing: { label: string; quantity: number }[],
        newRows: { label: string; quantity: number }[],
      ) => {
        const map = new Map<string, number>();
        existing.forEach((r) => map.set(r.label, r.quantity));
        newRows.forEach((r) =>
          map.set(r.label, (map.get(r.label) ?? 0) + r.quantity),
        );
        return Array.from(map.entries()).map(([label, quantity]) => ({
          label,
          quantity,
        }));
      };

      aggDeliveries = merge(aggDeliveries, deliveryValues);
      aggStorages = merge(aggStorages, storageValues);
    }

    // Parent line
    const rowsQty =
      aggDeliveries.reduce((sum, r) => sum + r.quantity, 0) +
      aggStorages.reduce((sum, r) => sum + r.quantity, 0);
    const parentQty = rowsQty;
    const parentLineId = nextId++;

    const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

    lines.push({
      id: parentLineId,
      displayNo: "",
      type: "Logo",
      price: 0,
      unitPrice: 0,
      adjustedPrice: 0,
      adjustedUnitPrice: 0,
      quantity: parentQty,
      title: logoTitle,
      displayImage: logoItem.displayImage?.lowResUrl ?? "",
      toStorage: logoItem.toStorage ?? 0,
      toDelivery: logoItem.toDelivery ?? 0,
      relatedTo: 0,
      product: {
        productNo: logoItem.baseLogo.product?.sku ?? "",
        productSku: logoItem.baseLogo.product?.sku,
        productType: "Logo",
        productName: logoTitle,
      },
      deliveryType:
        (logoItem.toDelivery ?? 0) > 0
          ? "Delivery"
          : (logoItem.toStorage ?? 0) > 0
            ? "Storage"
            : "Delivery",
      additionalFees: [],
      baseLogo: logoItem.baseLogo,
    } as OrderLineLogo);

    // We need to track the first emitted line ID to attach fees to it later
    // For grouped items, we track delivery lines by digit/label to associate FromStorage lines correctly
    const deliveryLines = new Map<string, number>();

    const emitLine = (
      label: string | number | undefined,
      qty: number,
      type: "Delivery" | "ToStorage" | "FromStorage",
      priceMultiplier: number = 1,
    ) => {
      const multiplier = priceMultiplier;
      if (qty <= 0) return;

      const isFromStorage = type === "FromStorage";
      const isToStorage = type === "ToStorage";

      const baseUnitPrice = getApplicablePrice(
        logoItem.baseLogo.product?.prices ?? [],
        globalLogoTotalQuantities.get(logoItem.baseLogo.id) ?? qty,
      );

      const unitPrice = isFromStorage
        ? baseUnitPrice * multiplier * -1
        : baseUnitPrice * multiplier;

      const lineId = nextId++;

      let productNo = logoItem.baseLogo.product?.sku ?? "";
      if (isFromStorage) productNo = "From Storage";
      else if (isToStorage) productNo = "To Storage";

      const logoTitle = formatLogoTitleWithSize(logoItem.title, logoItem.baseLogo);

      const baseTitle =
        label !== undefined && label !== ""
          ? `${logoTitle} (${label})`
          : logoTitle;
      const title = baseTitle;

      const lookupKey = label?.toString() ?? "";

      // Capture Delivery Line ID
      if (type === "Delivery") {
        deliveryLines.set(lookupKey, lineId);
      }

      // Associate FromStorage with corresponding Delivery line
      const deliveryLineId = deliveryLines.get(lookupKey);
      const relatedToId =
        isFromStorage && deliveryLineId ? deliveryLineId : parentLineId;

      lines.push({
        id: lineId,
        displayNo: "",
        type: "Logo",
        price: unitPrice,
        unitPrice: unitPrice,
        adjustedPrice: unitPrice,
        adjustedUnitPrice: unitPrice,
        quantity: qty,
        title: title,
        displayImage: undefined,
        toStorage: isToStorage ? qty : 0,
        toDelivery: !isToStorage ? qty : 0,
        relatedTo: relatedToId,
        product: {
          productNo: productNo,
          productSku: logoItem.baseLogo.product?.sku,
          productType: "Logo",
          productName: title,
        },
        deliveryType: isToStorage ? "Storage" : "Delivery",
        additionalFees: [],
        baseLogo: logoItem.baseLogo,
      } as OrderLineLogo);

      // Fees handled once per group at the end
    };

    // Tracking used storage for digits across multiple aggregated rows
    // const usedStorage = new Map<string, number>();

    const getAvailableStorage = (digit: string) => {
      if (isGroupNumberLogo) {
        return getGlobalAvailableStorage(logoItem.baseLogo, digit);
      } else {
        // For NameLogo or others, use main inStorage
        return getGlobalAvailableStorage(logoItem.baseLogo);
      }
    };

    const consumeStorage = (digit: string, amount: number) => {
      if (isGroupNumberLogo) {
        consumeGlobalStorage(logoItem.baseLogo, amount, digit);
      } else {
        consumeGlobalStorage(logoItem.baseLogo, amount);
      }
    };

    // Child lines: either from rows or fallback values
    aggDeliveries.forEach((row) => {
      const label = row.label; // e.g. "10"

      if (isGroupNumberLogo && label) {
        // Split number into digits
        const digits = label.toString().split("");
        digits.forEach((digit) => {
          // Each digit is a separate item of quantity `row.quantity`
          const qty = row.quantity;
          const avail = getAvailableStorage(digit);
          const fromStorage = Math.min(qty, avail);

          consumeStorage(digit, fromStorage);

          emitLine(digit, qty, "Delivery", 1);
          // Stamp the original full number on the just-emitted Delivery line so
          // the typed-numbers popover can collapse per-digit lines into one row
          // per typed number ("12", not "1" + "2"). FromStorage lines are not
          // groupable (filtered by isRealSku) so they don't need stamping.
          const lastDelivery = lines[lines.length - 1];
          if (lastDelivery && qty > 0) {
            lastDelivery.typedNumberValue = label.toString();
          }
          emitLine(digit, fromStorage, "FromStorage", 1);
        });
      } else {
        // NameLogo or non-split logic
        const qty = row.quantity;
        const avail = getAvailableStorage("");
        const fromStorage = Math.min(qty, avail);

        consumeStorage("", fromStorage);

        emitLine(label, qty, "Delivery", 1);
        emitLine(label, fromStorage, "FromStorage", 1);
      }
    });

    aggStorages.forEach((row) => {
      const label = row.label;
      if (isGroupNumberLogo && label) {
        const digits = label.toString().split("");
        digits.forEach((digit) => {
          emitLine(digit, row.quantity, "ToStorage", 1);
        });
      } else {
        emitLine(label, row.quantity, "ToStorage", 1);
      }
    });

    // One-time fee calculation for the entire group
    let totalNetProduction = 0;

    // Re-simulate logic to get net production
    const tempUsedStorage = new Map<string, number>();
    const getTempAvailable = (digit: string) => {
      const total = isGroupNumberLogo
        ? (logoItem.baseLogo.numberLogosInStorage?.find(
            (x) => x.value === digit,
          )?.inStorage ?? 0)
        : (logoItem.baseLogo.inStorage ?? 0);
      const used = tempUsedStorage.get(digit) ?? 0;
      return Math.max(0, total - used);
    };
    const consumeTemp = (digit: string, amt: number) => {
      tempUsedStorage.set(digit, (tempUsedStorage.get(digit) ?? 0) + amt);
    };

    aggDeliveries.forEach((row) => {
      if (isGroupNumberLogo && row.label) {
        row.label
          .toString()
          .split("")
          .forEach((digit) => {
            const avail = getTempAvailable(digit);
            const fromStorage = Math.min(row.quantity, avail);
            consumeTemp(digit, fromStorage);
            totalNetProduction += row.quantity - fromStorage;
          });
      } else {
        // NameLogo or others
        const key = isGroupNumberLogo ? "" : "main";
        const avail = getTempAvailable(key);
        const fromStorage = Math.min(row.quantity, avail);
        consumeTemp(key, fromStorage);
        totalNetProduction += row.quantity - fromStorage;
      }
    });

    aggStorages.forEach((row) => {
      if (isGroupNumberLogo && row.label) {
        totalNetProduction += row.quantity * row.label.toString().length;
      } else {
        totalNetProduction += row.quantity;
      }
    });

    if (totalNetProduction > 0 && lines.length > 0) {
      // Find a suitable parent line. First child line is okay.
      const firstChild = lines.find((l) => l.relatedTo === parentLineId);
      if (firstChild) {
        const current = globalFeeData.get(logoItem.baseLogo.id) ?? {
          netQty: 0,
          baseLogo: logoItem.baseLogo,
        };
        current.netQty += totalNetProduction;
        if (!current.parentLineId) {
          current.parentLineId = firstChild.id;
        }
        globalFeeData.set(logoItem.baseLogo.id, current);
      }
    }
  }

  // Generate all fee lines based on global net production
  globalFeeData.forEach((data) => {
    if (data.netQty > 0 && data.parentLineId) {
      const fees = createAdditionalFeeLines(
        data.parentLineId,
        data.baseLogo,
        data.netQty,
      );
      lines.push(...fees);
    }
  });

  // --- Delivery & Packaging Logic ---
  const deliveryType = options?.deliveryType ?? DeliveryType.Standard;
  const packagingType = options?.packaging ?? Packaging.NoPackaging;
  const deliveryBefore12PM = options?.deliveryBefore12PM ?? false;

  const totalItemsQuantity = lines
    .filter(
      (l) =>
        l.product.productType === "Design" && l.deliveryType === "Delivery",
    )
    .reduce((sum, l) => sum + l.quantity, 0);

  // 1. Packaging
  if (
    options?.packagingFees &&
    options.packagingFees.length > 0 &&
    (packagingType === Packaging.StandardBagPackaging ||
      packagingType === Packaging.SpecificPackaging)
  ) {
    options.packagingFees.forEach((fee) => {
      const isSpecific = packagingType === Packaging.SpecificPackaging;
      // For specific packaging, we might want to update the title to indicate adjustability
      const title = isSpecific
        ? `${fee.name} (QTY will be adjusted)`
        : fee.name;
      const totalCost = fee.price * fee.quantity;

      lines.push({
        id: nextId++,
        displayNo: "",
        type: "Packaging",
        price: fee.price,
        unitPrice: fee.price,
        adjustedPrice: fee.price,
        adjustedUnitPrice: fee.price,
        quantity: fee.quantity,
        title: title,
        toStorage: 0,
        toDelivery: fee.quantity,
        relatedTo: 0,
        product: {
          productNo: fee.itemNo,
          productType: "Packaging",
          productName: title,
        },
        deliveryType: "Delivery",
        additionalFees: [],
      } as OrderLine);
    });
  }

  // 2. Delivery Before 12PM
  // if (deliveryBefore12PM) {
  //   const cost = profile?.orderOptions?.orderOptionsDeliveryCost ?? 0;
  //   if (cost > 0) {
  //     lines.push({
  //       id: nextId++,
  //       displayNo: "",
  //       type: "AdditionalFee",
  //       price: cost,
  //       adjustedPrice: cost,
  //       adjustedUnitPrice: cost,
  //       quantity: 1,
  //       title: "Delivery before 12:00 PM with GLS",
  //       toStorage: 0,
  //       toDelivery: 1,
  //       relatedTo: 0,
  //       product: {
  //         productNo: "GLS12",
  //         productType: "Delivery",
  //         productName: "Delivery before 12:00 PM with GLS",
  //       },
  //       deliveryType: "Delivery",
  //       additionalFees: [],
  //     } as OrderLine);
  //   }
  // }

  // 3. Delivery Surcharge
  // Calculate subtotal for surcharge (excluding VAT, including other fees so far?)
  // Usually delivery fee is based on total goods value.
  const currentSubTotal = lines.reduce(
    (sum, l) => sum + (l.adjustedPrice || 0) * (l.quantity || 0),
    0,
  );

  let surchargePercent = 0;
  let expressTime = 24;
  if (deliveryType === DeliveryType.Express24) {
    surchargePercent = 1.0;
    expressTime = 24;
  } else if (deliveryType === DeliveryType.Express48) {
    surchargePercent = 0.5;
    expressTime = 48;
  } else if (deliveryType === DeliveryType.Express72) {
    surchargePercent = 0.25;
    expressTime = 72;
  }

  if (surchargePercent > 0) {
    const surcharge = currentSubTotal * surchargePercent;
    lines.push({
      id: nextId++,
      displayNo: "",
      type: "AdditionalFee",
      price: surcharge,
      adjustedPrice: surcharge,
      adjustedUnitPrice: surcharge,
      unitPrice: surcharge,
      quantity: 1,
      // The service is express PRODUCTION (produce within X hours); the percent
      // is the price surcharge. English on purpose: line titles are order DATA.
      title: `Express production ${expressTime} (${surchargePercent * 100}%)`,
      toStorage: 0,
      toDelivery: 1,
      relatedTo: 0,
      product: {
        productNo: `EXPRESS ${expressTime}`,
        productType: "Delivery",
        productName: `Express production ${expressTime} (${
          surchargePercent * 100
        }%)`,
      },
      deliveryType: "Delivery",
      additionalFees: [],
    } as OrderLine);
  }

  const grouped = groupAndSortLocally(lines);
  assignDisplayNumbers(grouped);

  const subTotal = grouped.reduce(
    (sum, l) => sum + (l.adjustedPrice || 0) * (l.quantity || 0),
    0,
  );
  // Keep in sync with useOrderConfirmationEditViewModel's vatRate.
  // Org rows store the rate as a PERCENT (DB: 25.00 = 25%, 0.00 = genuine
  // zero VAT / reverse charge — 148 orgs incl. DOS4U), so divide by 100.
  // `??` (not `||`): an EXPLICIT 0 is a legal rate and must show 0% — the
  // old ambiguity came from the draft's placeholder org carrying vatRate: 0,
  // which now carries undefined instead, so null/undefined = "profile org
  // not loaded yet" -> fall back to 25%.
  const vatRate = resolveVatFraction(organization?.vatRate);
  const vat = subTotal * vatRate;

  const now = new Date();

  return {
    id: 0,
    subTotal: subTotal,
    total: subTotal + vat,
    vat: vat,
    latestDelivery: now,
    createdAtUtc: now,
    updatedAtUtc: now,
    placedAtUtc: now,
    orderNumber: "",
    price: subTotal,
    currency: currency,
    status: OrderStatus.Draft,
    lines: grouped,
    heating: [],
    logoPrices: [],
    additionalFees: [],
    deliveryType: deliveryType,
    packagingType: packagingType,
    orderComment: "",
    originOfDelivery: "",
    requisitionNumber: "",
    organization: organization ?? {
      id: orgId,
      name: "",
      isVendor: false,
      excludedManufacturers: [],
      children: [],
      navNumber: "",
      country: "",
      disabled: false,
      hidePrices: false,
      type: "",
      contactPerson: "",
      contactEmail: "",
      contactPhone: "",
      address: "",
      cvr: "",
      totalOrderValue: 0,
      lastOrder: undefined,
      postCode: "",
      city: "",
      eori: "",
      shippingName: "",
      shippingAddress: "",
      shippingPostCode: "",
      shippingCity: "",
      shippingCountry: "",
      shippingContactPerson: "",
      shippingContactPhone: "",
      shippingContactEmail: "",
      parentId: undefined,
      mainBranchId: undefined,
      // undefined (NOT 0): an explicit 0 is a legal zero-VAT rate; the
      // placeholder must be distinguishable from it (see resolveVatFraction).
      vatRate: undefined,
      defaultVatRate: undefined,
      parent: undefined,
      companyLogo: undefined,
      email: "",
      phone: "",
      commission: undefined,
    },
    approval: null,
    orderDeliveryAddress: undefined,
    deliveryBefore12PMwithGLS: deliveryBefore12PM,
  };
}

function groupAndSortLocally(lines: OrderLine[]): OrderLine[] {
  const result: OrderLine[] = [];
  const tops = lines.filter((l) => !l.relatedTo);
  const designs = tops.filter((l) => l.product.productType === "Design");
  const logos = tops.filter((l) => l.product.productType === "Logo");
  const others = tops.filter(
    (l) => !["Design", "Logo"].includes(l.product.productType),
  );

  const appendWithChildren = (line: OrderLine) => {
    result.push(line);
    const kids = lines
      .filter((l) => l.relatedTo === line.id)
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    kids.forEach((k) => appendWithChildren(k));
  };

  [...designs, ...logos, ...others].forEach((top) => appendWithChildren(top));

  return result;
}

function assignDisplayNumbers(lines: OrderLine[]): void {
  const topLevels = lines.filter((l) => !l.relatedTo);
  topLevels.forEach((line, idx) => {
    line.displayNo = (idx + 1).toString().padStart(2, "0");
  });

  lines
    .filter((l) => l.relatedTo)
    .forEach((line) => {
      const parent = lines.find((l) => l.id === line.relatedTo);
      const siblings = lines.filter((l) => l.relatedTo === line.relatedTo);
      const siblingIndex = siblings.indexOf(line) + 1;
      line.displayNo = `${parent?.displayNo ?? ""} - ${siblingIndex}`;
    });
}
